import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import API_BASE_URL from '../config/api';

const LandingPage = () => {
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  
  // Refs for scroll animations
  const heroRef = useRef(null);
  const howItWorksRef = useRef(null);
  const pricingRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch schools from database
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        console.log('Fetching schools from:', `${API_BASE_URL}/api/schools/active`);
        const response = await fetch(`${API_BASE_URL}/api/schools/active`);
        
        // Check if response is ok
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(`Expected JSON but got ${contentType}. API URL might be incorrect: ${API_BASE_URL}`);
        }
        
        const result = await response.json();
        
        if (result.status === 'success') {
          const formattedSchools = result.data.map(school => ({
            name: school.school_name,
            code: school.school_code,
            flow_type: school.flow_type || (school.school_code === 'streamer-university' ? 'streamer' : 'standard')
          }));
          setSchools(formattedSchools);
        } else {
          console.error('Failed to fetch schools:', result.message);
          // Fallback to empty array or show error
          setSchools([]);
        }
      } catch (error) {
        console.error('Error fetching schools:', error);
        console.error('Current API_BASE_URL:', API_BASE_URL);
        console.error('Make sure REACT_APP_API_URL is set in your .env file or Vercel environment variables');
        // Fallback to empty array
        setSchools([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, []);

  // Entrance animation on mount
  useEffect(() => {
    // Trigger entrance animations after component mounts
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe sections
    if (howItWorksRef.current) observer.observe(howItWorksRef.current);
    if (pricingRef.current) observer.observe(pricingRef.current);

    return () => observer.disconnect();
  }, []);

  const handleGetStarted = () => {
    if (!selectedSchool) return;

    const isStreamerFlow =
      selectedSchool.code === 'streamer-university' ||
      selectedSchool.flow_type === 'streamer';

    if (isStreamerFlow) {
      navigate('/streamer/submit');
      return;
    }

    navigate(`/submit/${selectedSchool.code}`);
  };

  // Handle click on dropdown wrapper - scroll first if needed, then open
  const handleDropdownWrapperClick = (e) => {
    if (dropdownRef.current) {
      const dropdownElement = dropdownRef.current.getElement();
      if (dropdownElement) {
        const rect = dropdownElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        
        // If there's not enough space below (less than 300px for dropdown panel)
        if (spaceBelow < 300) {
          e.preventDefault();
          e.stopPropagation();
          
          const scrollAmount = 300 - spaceBelow + 20;
          window.scrollBy({ top: scrollAmount, behavior: 'smooth' });
          
          // After scroll animation, open the dropdown
          setTimeout(() => {
            if (dropdownRef.current) {
              dropdownRef.current.show();
            }
          }, 350);
        }
      }
    }
  };

  return (
    <div className="landing-page">
      {/* Header Navigation */}
      <header className={`landing-header ${isVisible ? 'header-visible' : ''}`}>
        <div className="header-content">
          <div className={`logo ${isVisible ? 'logo-visible' : ''}`}>
            {/* <div className="logo-icon">🏫</div> */}
            <span className="logo-text">UniPostings</span>
          </div>
          <nav className={`nav-menu ${isVisible ? 'nav-visible' : ''}`}>
            <a href="#how-it-works" className="nav-item">Guide</a>
            <span className="nav-separator"></span>
            <a href="#pricing" className="nav-item">Pricing</a>
            <span className="nav-separator"></span>
            <a href="#contact" className="nav-item">Contact</a>
            <span className="nav-separator"></span>
            <Button 
              label="Create Post" 
              className="header-cta"
              onClick={() => selectedSchool && handleGetStarted()}
              disabled={!selectedSchool}
            />
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section" ref={heroRef}>
        <div className={`hero-content ${isVisible ? 'hero-visible' : ''}`}>
          <div className="hero-text">
            <h1 className={`hero-title ${isVisible ? 'title-visible' : ''}`}>
              Connect with Your
              <span className="highlight"> Freshman Class</span>
            </h1>
            <p className={`hero-description ${isVisible ? 'description-visible' : ''}`}>
              Get featured on our Instagram to meet fellow students!
              Join thousands of students who've already found their community.
            </p>
            
            {/* School Selection */}
            <div className={`school-selection ${isVisible ? 'selection-visible' : ''}`}>
              <label className="school-label">Select School</label>
              <div onClickCapture={handleDropdownWrapperClick}>
                <Dropdown
                  ref={dropdownRef}
                  value={selectedSchool}
                  onChange={(e) => setSelectedSchool(e.value)}
                  options={schools}
                  optionLabel="name"
                  placeholder={loading ? "Loading schools..." : "Choose school..."}
                  className="school-dropdown"
                  showClear={false}
                  filter={true}
                  filterPlaceholder="Search schools..."
                  panelClassName="modern-dropdown-panel"
                  appendTo="self"
                  disabled={loading}
                  itemTemplate={(option) => (
                    <div className="dropdown-option">
                      <span className="option-name">{option.name}</span>
                    </div>
                  )}
                />
              </div>
              <Button
                label="Create Post"
                className="hero-cta primary"
                onClick={handleGetStarted}
                disabled={!selectedSchool}
              />
            </div>

            {/* Social Proof */}
            <div className={`social-proof ${isVisible ? 'proof-visible' : ''}`}>
              <div className="proof-item">
                <span className="proof-dot primary"></span>
                <span className="proof-text">1,500+ Students Connected</span>
              </div>
              <div className="proof-item">
                <span className="proof-dot secondary"></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works scroll-animate" ref={howItWorksRef}>
        <div className="container">
          <h2>How It Works</h2>
          <div className="steps-grid">
            <div className="step-card" style={{ animationDelay: '0.1s' }}>
              <div className="step-number">1</div>
              <h3>Select Your School</h3>
              <p>Choose your university from our verified list</p>
            </div>
            <div className="step-card" style={{ animationDelay: '0.2s' }}>
              <div className="step-number">2</div>
              <h3>Fill Out Your Profile</h3>
              <p>Share your story, photos, and interests</p>
            </div>
            <div className="step-card" style={{ animationDelay: '0.3s' }}>
              <div className="step-number">3</div>
              <h3>Get Featured</h3>
              <p>We'll post you on Instagram to connect with classmates</p>
            </div>
            <div className="step-card" style={{ animationDelay: '0.4s' }}>
              <div className="step-number">4</div>
              <h3>Start Connecting</h3>
              <p>Meet new friends and build your university community</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section scroll-animate" ref={pricingRef}>
        <div className="container">
          <h2>Simple Instant Posting</h2>
          <p className="pricing-subtitle">One clear option — your post goes live right away</p>
          
          <div className="pricing-tiers pricing-tiers-single">
            <div className="pricing-tier-card popular">
              <div className="tier-badge popular">Instant</div>
              <h3>$5</h3>
              <p className="tier-description">Post immediately</p>
              <ul className="tier-features">
                <li>✓ No wait / no queue</li>
                <li>✓ Highest priority</li>
                <li>✓ Professional Instagram feature</li>
              </ul>
            </div>
          </div>

          <div className="pricing-note">
            <p><strong>Included:</strong> Professional profile creation, Instagram feature post, and connection with classmates</p>
            <p className="pricing-disclaimer">Select your school and start your submission to check out.</p>
          </div>

          <Button
            label="Get Started"
            className="pricing-cta"
            onClick={() => selectedSchool && handleGetStarted()}
            disabled={!selectedSchool}
          />
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Contact Us</h4>
              <p>hello@UniPostings.com</p>
              <p>Questions? We're here to help!</p>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <p><a href="/privacy-policy" className="footer-link">Privacy Policy</a></p>
              <p><a href="/terms-of-service" className="footer-link">Terms of Service</a></p>
            </div>
            <div className="footer-section">
              <h4>Follow Us</h4>
              <p>@UniPostings on Instagram</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 UniPostings LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 